// src/models/TelephonyProfile.js
// Telephony profile model for per-tenant voice and SIP preferences.

const DEFAULT_SIP_STATUS = 'not_started';
const DEFAULT_FORWARDING_STATUS = 'not_applicable';

const normalizeString = (value) => {
  if (value === undefined) {
    return null;
  }

  if (value === null) {
    return null;
  }

  return typeof value === 'string' ? value.trim() || null : null;
};

const sanitizeBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return fallback;
};

export class TelephonyProfile {
  constructor(data = {}) {
    this.phoneNumberE164 = normalizeString(data.phoneNumberE164 ?? data.phone_number_e164);
    this.numberType = normalizeString(data.numberType ?? data.number_type);
    this.countryIso2 = normalizeString(data.countryIso2 ?? data.country_iso2);

    const sip = data.sip || {};
    const forwarding = data.forwarding || {};

    this.sip = {
      desired: sanitizeBoolean(sip.desired, false),
      pbxVendor: normalizeString(sip.pbxVendor),
      carrier: normalizeString(sip.carrier),
      routingPolicy: normalizeString(sip.routingPolicy),
      status: normalizeString(sip.status) || DEFAULT_SIP_STATUS,
      lastUpdated: typeof sip.lastUpdated === 'number' ? sip.lastUpdated : Date.now()
    };

    this.forwarding = {
      desired: sanitizeBoolean(forwarding.desired, false),
      status: normalizeString(forwarding.status) || DEFAULT_FORWARDING_STATUS,
      notes: normalizeString(forwarding.notes)
    };
  }

  static createDefault() {
    return new TelephonyProfile({
      phoneNumberE164: null,
      numberType: null,
      countryIso2: null,
      sip: {
        desired: false,
        pbxVendor: null,
        carrier: null,
        routingPolicy: null,
        status: DEFAULT_SIP_STATUS,
        lastUpdated: Date.now()
      },
      forwarding: {
        desired: false,
        status: DEFAULT_FORWARDING_STATUS,
        notes: null
      }
    });
  }

  static fromFirestore(data) {
    if (!data || typeof data !== 'object') {
      return TelephonyProfile.createDefault();
    }

    return new TelephonyProfile(data);
  }

  toFirestore() {
    return {
      phoneNumberE164: this.phoneNumberE164 || null,
      numberType: this.numberType || null,
      countryIso2: this.countryIso2 || null,
      sip: {
        desired: this.sip.desired,
        pbxVendor: this.sip.pbxVendor || null,
        carrier: this.sip.carrier || null,
        routingPolicy: this.sip.routingPolicy || null,
        status: this.sip.status || DEFAULT_SIP_STATUS,
        lastUpdated: this.sip.lastUpdated || Date.now()
      },
      forwarding: {
        desired: this.forwarding.desired,
        status: this.forwarding.status || DEFAULT_FORWARDING_STATUS,
        notes: this.forwarding.notes || null
      }
    };
  }

  withNumberPreferences({ phoneNumberE164, numberType, countryIso2 }) {
    return new TelephonyProfile({
      ...this.toFirestore(),
      phoneNumberE164: normalizeString(phoneNumberE164),
      numberType: normalizeString(numberType),
      countryIso2: normalizeString(countryIso2)
    });
  }

  withSipUpdates(updates = {}) {
    const mergedSip = {
      ...this.sip,
      ...updates
    };

    if (!updates || updates.lastUpdated === undefined) {
      mergedSip.lastUpdated = Date.now();
    }

    return new TelephonyProfile({
      ...this.toFirestore(),
      sip: mergedSip
    });
  }

  withForwardingUpdates(updates = {}) {
    const mergedForwarding = {
      ...this.forwarding,
      ...updates
    };

    return new TelephonyProfile({
      ...this.toFirestore(),
      forwarding: mergedForwarding
    });
  }

  merge(updates = {}) {
    let profile = this;

    if (
      updates.phoneNumberE164 !== undefined ||
      updates.numberType !== undefined ||
      updates.countryIso2 !== undefined
    ) {
      profile = profile.withNumberPreferences({
        phoneNumberE164: updates.phoneNumberE164 ?? this.phoneNumberE164,
        numberType: updates.numberType ?? this.numberType,
        countryIso2: updates.countryIso2 ?? this.countryIso2
      });
    }

    if (updates.sip) {
      profile = profile.withSipUpdates(updates.sip);
    }

    if (updates.forwarding) {
      profile = profile.withForwardingUpdates(updates.forwarding);
    }

    return profile;
  }

  isBusinessNumber() {
    return this.numberType === 'business';
  }

  isPersonalNumber() {
    return this.numberType === 'personal';
  }

  requiresRogersSIPNote() {
    if (!this.countryIso2 || !this.sip.carrier) {
      return false;
    }

    return (
      this.countryIso2.toUpperCase() === 'CA' &&
      this.sip.carrier.toLowerCase().includes('rogers')
    );
  }
}
